import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  ImageList,
  ImageListItem,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SyncIcon from '@mui/icons-material/Sync';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';
import DetailModalShell from '../components/detail/DetailModalShell';
import MockupsDataTabs from '../components/detail/MockupsDataTabs';

const debounceDelay = 300;
const defaultRowsPerPage = 25;

const TABLE_COLUMNS = [
  { key: 'listing', label: 'Listing', minWidth: 260, maxWidth: 560, defaultWidth: 340 },
  { key: 'status', label: 'Status', minWidth: 110, maxWidth: 220, defaultWidth: 130 },
  { key: 'type', label: 'Type', minWidth: 100, maxWidth: 200, defaultWidth: 120 },
  { key: 'sku', label: 'SKU', minWidth: 150, maxWidth: 360, defaultWidth: 210 },
  { key: 'price', label: 'Price', minWidth: 100, maxWidth: 200, defaultWidth: 120 },
  { key: 'stock', label: 'Stock', minWidth: 120, maxWidth: 240, defaultWidth: 140 },
  { key: 'sales', label: 'Sales', minWidth: 90, maxWidth: 180, defaultWidth: 100 },
  { key: 'printify', label: 'Printify', minWidth: 120, maxWidth: 220, defaultWidth: 140 },
  { key: 'updated', label: 'Updated', minWidth: 170, maxWidth: 340, defaultWidth: 210 },
];

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'publish', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'private', label: 'Private' },
  { value: 'trash', label: 'Trash' },
];

const printifyLinkOptions = [
  { value: '', label: 'All links' },
  { value: 'linked', label: 'Linked' },
  { value: 'unlinked', label: 'Unlinked' },
];

function getPreviewUrl(item) {
  return item?.thumbnail_url || item?.image_url || '';
}

function formatPrice(item) {
  if (!item?.price) {
    return '—';
  }
  return `$${item.price}`;
}

function formatDate(value) {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
}

function termLabel(item) {
  if (!item) {
    return '';
  }
  if (typeof item === 'string') {
    return item;
  }
  return item.name || item.slug || String(item.id || '');
}

function stripHTML(value) {
  if (!value || typeof value !== 'string') {
    return '';
  }
  if (typeof document !== 'undefined') {
    const element = document.createElement('div');
    element.innerHTML = value;
    return (element.textContent || element.innerText || '').trim();
  }
  return value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function getImageFilename(url) {
  if (!url) {
    return '';
  }
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split('/').filter(Boolean);
    return segments[segments.length - 1] || url;
  } catch {
    const segments = url.split('/').filter(Boolean);
    return segments[segments.length - 1] || url;
  }
}

const WooCommerceListingsPage = () => {
  const { fetchWithAuth } = useAuth();
  const [sites, setSites] = React.useState([]);
  const [sitesLoading, setSitesLoading] = React.useState(false);
  const [selectedSiteId, setSelectedSiteId] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState('');
  const [selectedPrintifyLink, setSelectedPrintifyLink] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [linking, setLinking] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [actionMessage, setActionMessage] = React.useState(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailListing, setDetailListing] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: 'table.woocommerce-listings',
  });

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), debounceDelay);
    return () => window.clearTimeout(handle);
  }, [search]);

  React.useEffect(() => {
    setPage(0);
  }, [debouncedSearch, rowsPerPage, selectedSiteId, selectedStatus, selectedPrintifyLink]);

  const loadSites = React.useCallback(async () => {
    setSitesLoading(true);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/sites`);
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load WooCommerce sites');
      }
      const payload = await resp.json();
      const nextSites = Array.isArray(payload.items) ? payload.items : [];
      setSites(nextSites);
      setSelectedSiteId((current) => (
        current && nextSites.some((site) => site.id === current) ? current : nextSites[0]?.id || ''
      ));
    } catch (err) {
      setError(err.message || 'Failed to load WooCommerce sites');
    } finally {
      setSitesLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadSites();
  }, [loadSites]);

  const loadListings = React.useCallback(async () => {
    if (!selectedSiteId) {
      setItems([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('site_id', selectedSiteId);
      params.set('limit', rowsPerPage.toString());
      params.set('offset', (page * rowsPerPage).toString());
      if (debouncedSearch) {
        params.set('q', debouncedSearch);
      }
      if (selectedStatus) {
        params.set('status', selectedStatus);
      }
      if (selectedPrintifyLink) {
        params.set('printify_link', selectedPrintifyLink);
      }
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/listings?${params.toString()}`);
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load WooCommerce listings');
      }
      const payload = await resp.json();
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setTotal(typeof payload.total === 'number' ? payload.total : 0);
    } catch (err) {
      setError(err.message || 'Failed to load WooCommerce listings');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, fetchWithAuth, page, rowsPerPage, selectedPrintifyLink, selectedSiteId, selectedStatus]);

  React.useEffect(() => {
    loadListings();
  }, [loadListings]);

  const handleSync = async () => {
    if (!selectedSiteId) {
      return;
    }
    setSyncing(true);
    setError(null);
    setActionMessage(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/listings/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: selectedSiteId }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to sync WooCommerce listings');
      }
      setActionMessage(
        `Synced ${payload.listings_synced ?? 0} listing${payload.listings_synced === 1 ? '' : 's'}.`,
      );
      await loadListings();
    } catch (err) {
      setError(err.message || 'Failed to sync WooCommerce listings');
    } finally {
      setSyncing(false);
    }
  };

  const handleLinkPrintify = async () => {
    if (!selectedSiteId) {
      return;
    }
    setLinking(true);
    setError(null);
    setActionMessage(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/listings/link-printify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: selectedSiteId }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to link Printify products');
      }
      const linkedTotal = (payload.linked ?? 0) + (payload.relinked ?? 0);
      setActionMessage(
        `Linked ${linkedTotal} listing${linkedTotal === 1 ? '' : 's'} to Printify. ` +
          `${payload.already_linked ?? 0} already linked, ${payload.no_match ?? 0} without SKU matches, ` +
          `${payload.ambiguous ?? 0} ambiguous.`,
      );
      await loadListings();
    } catch (err) {
      setError(err.message || 'Failed to link Printify products');
    } finally {
      setLinking(false);
    }
  };

  const openDetail = async (listing) => {
    if (!listing?.id) {
      return;
    }
    setDetailOpen(true);
    setDetailListing(listing);
    setDetailLoading(true);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/listings/${listing.id}`);
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load listing');
      }
      const payload = await resp.json();
      if (payload.item) {
        setDetailListing(payload.item);
      }
    } catch (err) {
      setError(err.message || 'Failed to load listing');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleListingUpdated = React.useCallback((updatedListing) => {
    if (!updatedListing?.id) {
      return;
    }
    setDetailListing(updatedListing);
    setItems((previous) => previous.map((item) => (
      item.id === updatedListing.id ? { ...item, ...updatedListing } : item
    )));
  }, []);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          select
          label="WooCommerce Site"
          value={selectedSiteId}
          onChange={(event) => setSelectedSiteId(event.target.value)}
          sx={{ minWidth: 260 }}
          disabled={sitesLoading || sites.length === 0}
        >
          {sites.length === 0 ? (
            <MenuItem value="" disabled>
              {sitesLoading ? 'Loading…' : 'No sites'}
            </MenuItem>
          ) : (
            sites.map((site) => (
              <MenuItem key={site.id} value={site.id}>
                {site.name || site.url}
              </MenuItem>
            ))
          )}
        </TextField>
        <TextField
          fullWidth
          placeholder="Search listings"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        <TextField
          select
          label="Status"
          value={selectedStatus}
          onChange={(event) => setSelectedStatus(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Printify"
          value={selectedPrintifyLink}
          onChange={(event) => setSelectedPrintifyLink(event.target.value)}
          sx={{ minWidth: 160 }}
        >
          {printifyLinkOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          startIcon={syncing ? <CircularProgress color="inherit" size={16} /> : <SyncIcon />}
          onClick={handleSync}
          disabled={!selectedSiteId || syncing}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {syncing ? 'Syncing…' : 'Sync Listings'}
        </Button>
        <Button
          variant="outlined"
          startIcon={linking ? <CircularProgress color="inherit" size={16} /> : <LinkIcon />}
          onClick={handleLinkPrintify}
          disabled={!selectedSiteId || linking}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {linking ? 'Linking…' : 'Link Printify'}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {actionMessage ? <Alert severity="success">{actionMessage}</Alert> : null}

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }} stickyHeader>
            <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  <ResizableHeaderCell
                    key={column.key}
                    column={column}
                    getWidth={getWidth}
                    onResizeStart={startColumnResize}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading WooCommerce listings..." />
              ) : items.length === 0 ? (
                <TableEmptyRow colSpan={TABLE_COLUMNS.length} label="No listings found." />
              ) : (
                items.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openDetail(item)}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Avatar
                          src={getPreviewUrl(item)}
                          alt=""
                          variant="rounded"
                          sx={{ width: 44, height: 44, flex: '0 0 auto' }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" noWrap title={item.title}>
                            {item.title || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {item.external_id}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.status || '—'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.type || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={item.sku}>
                        {item.sku || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatPrice(item)}</TableCell>
                    <TableCell>{item.stock_status || '—'}</TableCell>
                    <TableCell>{item.total_sales ?? 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.has_printify_product ? 'Linked' : 'Unlinked'}
                        size="small"
                        color={item.has_printify_product ? 'success' : 'default'}
                        variant={item.has_printify_product ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(item.updated_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <WooCommerceListingDetailDialog
        open={detailOpen}
        listing={detailListing}
        loading={detailLoading}
        onClose={() => setDetailOpen(false)}
        fetchWithAuth={fetchWithAuth}
        onListingUpdated={handleListingUpdated}
      />
    </Box>
  );
};

const WooCommerceListingDetailDialog = ({ open, listing, loading, onClose, fetchWithAuth, onListingUpdated }) => {
  const fileInputRef = React.useRef(null);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [deletingImageId, setDeletingImageId] = React.useState(null);
  const [featuringImageId, setFeaturingImageId] = React.useState(null);
  const [deletingSelectedImages, setDeletingSelectedImages] = React.useState(false);
  const [imageError, setImageError] = React.useState(null);
  const [imageInfo, setImageInfo] = React.useState(null);
  const [selectedImageIds, setSelectedImageIds] = React.useState(() => new Set());
  const [detailTab, setDetailTab] = React.useState('mockups');

  const images = React.useMemo(() => (
    listing && Array.isArray(listing.images) ? listing.images : []
  ), [listing]);
  const categories = React.useMemo(() => (
    listing && Array.isArray(listing.categories) ? listing.categories : []
  ), [listing]);
  const tags = React.useMemo(() => (
    listing && Array.isArray(listing.tags) ? listing.tags : []
  ), [listing]);
  const rawData = React.useMemo(() => listing?.raw_data || listing?.RawData || null, [listing]);
  const description = React.useMemo(() => stripHTML(listing?.description), [listing?.description]);
  const shortDescription = React.useMemo(() => stripHTML(listing?.short_description), [listing?.short_description]);

  React.useEffect(() => {
    setImageError(null);
    setImageInfo(null);
    setUploadingImage(false);
    setDeletingImageId(null);
    setFeaturingImageId(null);
    setDeletingSelectedImages(false);
    setSelectedImageIds(new Set());
    setDetailTab('mockups');
  }, [listing?.id]);

  const handleUploadClick = React.useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = React.useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file || !listing?.id) {
      return;
    }
    setUploadingImage(true);
    setImageError(null);
    setImageInfo(null);
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (images.length > 0) {
        formData.append('rank', (images.length + 1).toString());
      }
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/listings/${listing.id}/images`, {
        method: 'POST',
        body: formData,
        timeoutMs: config.longRequestTimeoutMs,
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Upload failed');
      }
      if (payload.listing) {
        onListingUpdated?.(payload.listing);
      }
      setImageInfo('Image uploaded successfully.');
    } catch (err) {
      setImageError(err.message || 'Upload failed');
    } finally {
      setUploadingImage(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  }, [fetchWithAuth, images.length, listing?.id, onListingUpdated]);

  const handleFeatureImage = React.useCallback(async (image) => {
    if (!image?.id || !listing?.id) {
      return;
    }
    if ((image.rank || image.position + 1) === 1) {
      return;
    }
    setFeaturingImageId(image.id);
    setImageError(null);
    setImageInfo(null);
    try {
      const resp = await fetchWithAuth(
        `${config.apiBaseUrl}/woocommerce/listings/${listing.id}/images/${image.id}/feature`,
        {
          method: 'POST',
          timeoutMs: config.longRequestTimeoutMs,
        },
      );
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to update featured image');
      }
      if (payload.listing) {
        onListingUpdated?.(payload.listing);
      }
      setImageInfo('Featured image updated.');
    } catch (err) {
      setImageError(err.message || 'Failed to update featured image');
    } finally {
      setFeaturingImageId(null);
    }
  }, [fetchWithAuth, listing?.id, onListingUpdated]);

  const handleDeleteImage = React.useCallback(async (imageId) => {
    if (!imageId || !listing?.id) {
      return;
    }
    setDeletingImageId(imageId);
    setImageError(null);
    setImageInfo(null);
    try {
      const resp = await fetchWithAuth(
        `${config.apiBaseUrl}/woocommerce/listings/${listing.id}/images/${imageId}`,
        {
          method: 'DELETE',
          timeoutMs: config.longRequestTimeoutMs,
        },
      );
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Delete failed');
      }
      if (payload.listing) {
        onListingUpdated?.(payload.listing);
      }
      setSelectedImageIds((previous) => {
        if (!previous.has(imageId)) {
          return previous;
        }
        const next = new Set(previous);
        next.delete(imageId);
        return next;
      });
      setImageInfo('Image removed from this listing.');
    } catch (err) {
      setImageError(err.message || 'Delete failed');
    } finally {
      setDeletingImageId(null);
    }
  }, [fetchWithAuth, listing?.id, onListingUpdated]);

  const imageIds = React.useMemo(() => images.map((image) => image.id).filter(Boolean), [images]);
  const selectedImageCount = selectedImageIds.size;
  const visibleSelectedImageCount = React.useMemo(
    () => imageIds.filter((id) => selectedImageIds.has(id)).length,
    [imageIds, selectedImageIds],
  );
  const allImagesSelected = imageIds.length > 0 && visibleSelectedImageCount === imageIds.length;
  const someImagesSelected = visibleSelectedImageCount > 0 && visibleSelectedImageCount < imageIds.length;

  const handleToggleAllImages = React.useCallback((event) => {
    if (event.target.checked) {
      setSelectedImageIds(new Set(imageIds));
    } else {
      setSelectedImageIds(new Set());
    }
  }, [imageIds]);

  const handleToggleImage = React.useCallback((imageId) => {
    if (!imageId) {
      return;
    }
    setSelectedImageIds((previous) => {
      const next = new Set(previous);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  }, []);

  const handleDeleteSelectedImages = React.useCallback(async () => {
    if (!listing?.id || selectedImageIds.size === 0) {
      return;
    }
    setDeletingSelectedImages(true);
    setImageError(null);
    setImageInfo(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/listings/${listing.id}/images/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_ids: Array.from(selectedImageIds) }),
        timeoutMs: config.longRequestTimeoutMs,
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to delete selected images');
      }
      if (payload.listing) {
        onListingUpdated?.(payload.listing);
      }
      setSelectedImageIds(new Set());
      setImageInfo(`Removed ${selectedImageIds.size} image${selectedImageIds.size === 1 ? '' : 's'} from this listing.`);
    } catch (err) {
      setImageError(err.message || 'Failed to delete selected images');
    } finally {
      setDeletingSelectedImages(false);
    }
  }, [fetchWithAuth, listing?.id, onListingUpdated, selectedImageIds]);

  return (
    <DetailModalShell
      open={open}
      onClose={onClose}
      title={loading ? 'Loading listing...' : listing?.title || listing?.name || 'WooCommerce Listing'}
      externalId={listing?.external_id || listing?.id}
      left={
        loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
            <CircularProgress size={24} />
          </Box>
        ) : listing ? (
          <MockupsDataTabs
            value={detailTab}
            onChange={setDetailTab}
            rawData={rawData}
            mockupsToolbar={
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2">Media</Typography>
                  <Chip size="small" label={`${images.length} image${images.length === 1 ? '' : 's'}`} />
                  <Checkbox
                    size="small"
                    indeterminate={someImagesSelected}
                    checked={allImagesSelected}
                    onChange={handleToggleAllImages}
                    disabled={imageIds.length === 0}
                    inputProps={{ 'aria-label': 'select all listing images' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Select images
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteSelectedImages}
                    disabled={deletingSelectedImages || selectedImageCount === 0 || !listing?.id}
                  >
                    {deletingSelectedImages ? 'Deleting...' : `Delete (${selectedImageCount})`}
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    onClick={handleUploadClick}
                    disabled={uploadingImage || !listing?.id}
                  >
                    {uploadingImage ? 'Uploading...' : 'Upload Image'}
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </Stack>
              </Stack>
            }
            mockupsContent={
              <>
                {images.length > 0 ? (
                  <ImageList
                    cols={3}
                    gap={8}
                    sx={{
                      m: 0,
                      maxHeight: 'calc(100vh - 240px)',
                      overflowY: 'auto',
                      pr: 1,
                      '& img': {
                        width: '100%',
                        aspectRatio: '1 / 1',
                        objectFit: 'cover',
                        borderRadius: 2,
                        display: 'block',
                        bgcolor: 'grey.100',
                      },
                    }}
                  >
                    {images.map((image, index) => {
                      const imageUrl = image.url || image.src || '';
                      const rank = image.rank || image.position + 1 || index + 1;
                      const isFeatured = rank === 1;
                      return (
                        <ImageListItem key={`image-${image.id || image.external_id || index}`} sx={{ position: 'relative' }}>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              bgcolor: 'rgba(0,0,0,0.65)',
                              color: '#fff',
                              borderRadius: 1,
                              px: 1,
                              py: 0.25,
                              fontSize: 12,
                              fontWeight: 500,
                              zIndex: 1,
                            }}
                          >
                            #{rank}
                          </Box>
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={image.alt || image.name || listing?.title || 'WooCommerce listing image'}
                            title={getImageFilename(imageUrl)}
                            loading="lazy"
                          />
                          <IconButton
                            size="small"
                            aria-label={isFeatured ? 'Featured image' : 'Set as featured image'}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 80,
                              bgcolor: 'rgba(0,0,0,0.55)',
                              color: '#fff',
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleFeatureImage(image);
                            }}
                            disabled={isFeatured || featuringImageId === image.id || !listing?.id}
                          >
                            {isFeatured ? (
                              <StarIcon fontSize="small" />
                            ) : featuringImageId === image.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <StarBorderIcon fontSize="small" />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={`Select image ${rank}`}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 44,
                              bgcolor: 'rgba(255,255,255,0.85)',
                              borderRadius: 1,
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleToggleImage(image.id);
                            }}
                            disabled={!listing?.id}
                          >
                            <Checkbox
                              size="small"
                              checked={selectedImageIds.has(image.id)}
                              sx={{ p: 0.25 }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={`Delete image ${rank}`}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(0,0,0,0.55)',
                              color: '#fff',
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                            }}
                            onClick={() => handleDeleteImage(image.id)}
                            disabled={deletingImageId === image.id || !listing?.id}
                          >
                            {deletingImageId === image.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </ImageListItem>
                      );
                    })}
                  </ImageList>
                ) : getPreviewUrl(listing) ? (
                  <Box
                    component="img"
                    src={getPreviewUrl(listing)}
                    alt={listing?.title || 'WooCommerce listing preview'}
                    sx={{ width: '100%', borderRadius: 2 }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      minHeight: 220,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    No media available
                  </Box>
                )}
                {imageError ? (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {imageError}
                  </Alert>
                ) : null}
                {imageInfo ? (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {imageInfo}
                  </Alert>
                ) : null}
              </>
            }
          />
        ) : (
          <Typography color="text.secondary">No listing selected.</Typography>
        )
      }
      right={
        loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
            <CircularProgress size={24} />
          </Box>
        ) : listing ? (
          <Stack spacing={1.5} sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                label={listing.status || '—'}
                color={listing.status === 'publish' ? 'success' : 'default'}
                size="small"
              />
              <Chip label={listing.type || '—'} size="small" variant="outlined" />
              <Chip label={`Stock: ${listing.stock_status || '—'}`} size="small" variant="outlined" />
              <Chip label={`Sales: ${listing.total_sales ?? 0}`} size="small" variant="outlined" />
              <Chip
                label={listing.has_printify_product ? 'Printify: Linked' : 'Printify: Unlinked'}
                size="small"
                color={listing.has_printify_product ? 'success' : 'default'}
                variant={listing.has_printify_product ? 'filled' : 'outlined'}
              />
              {listing.url ? (
                <Button
                  size="small"
                  variant="outlined"
                  component="a"
                  href={listing.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenInNewIcon />}
                >
                  View
                </Button>
              ) : null}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Updated: {formatDate(listing.updated_at)}
            </Typography>
            <Stack spacing={1}>
              <InfoRow label="Price" value={formatPrice(listing)} />
              <InfoRow label="Regular Price" value={listing.regular_price ? `$${listing.regular_price}` : '—'} />
              <InfoRow label="Sale Price" value={listing.sale_price ? `$${listing.sale_price}` : '—'} />
              <InfoRow label="SKU" value={listing.sku || '—'} />
              <InfoRow label="Visibility" value={listing.catalog_visibility || '—'} />
              <InfoRow label="Purchasable" value={listing.purchasable ? 'Yes' : 'No'} />
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Typography variant="subtitle2">Description</Typography>
              {description ? (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {description}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No description provided.
                </Typography>
              )}
            </Stack>
            {shortDescription ? (
              <>
                <Divider />
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Short Description</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {shortDescription}
                  </Typography>
                </Stack>
              </>
            ) : null}
            <Divider />
            <Stack spacing={1}>
              <Typography variant="subtitle2">Categories</Typography>
              {categories.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {categories.map((category) => {
                    const label = termLabel(category);
                    return label ? <Chip key={`${category.id || label}`} label={label} size="small" /> : null;
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No categories set.
                </Typography>
              )}
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Typography variant="subtitle2">Tags</Typography>
              {tags.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {tags.map((tag) => {
                    const label = termLabel(tag);
                    return label ? <Chip key={`${tag.id || label}`} label={label} size="small" /> : null;
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No tags set.
                </Typography>
              )}
            </Stack>
          </Stack>
        ) : (
          <Typography color="text.secondary">No listing selected.</Typography>
        )
      }
    />
  );
};

const InfoRow = ({ label, value }) => (
  <Stack direction="row" spacing={1} justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ textAlign: 'right', overflowWrap: 'anywhere' }}>
      {value ?? '—'}
    </Typography>
  </Stack>
);

export default WooCommerceListingsPage;
